import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import ChatIntro from '../../../Chat/MessagesArea/ChatIntro'
import ScoutGeneralDashboard from '../../GeneralDashboard/index.js'
import ScoutAccountDashboard from '../../AccountDashboard/index.js'
import { lockedReducer } from '../../../../utils/defaultReducer'
import api from '../../../../api'
import useNotification from '../../../Notification'
import { getSession } from '../../../../auth'
import { groupIcons, transformTimeData } from '../ChatList'
import {
    Box, Avatar, List, ListItem, Tooltip, Button
} from '@mui/material'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExportIcon from '@mui/icons-material/GetApp';


import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { ShowComponent } from '../../../../utils/isVisible'
import EmailDialog from '../EmailDialogs.js'
import EditList from '../EditList/index.js'
import ReactionsList from '../ReactionsList/index.js'

import ScoutMessageComponent from '../ScoutMessageComponent/index.js'

import dayjs from 'dayjs'

import { chatName } from '../../../../utils/scout'


const defaultLimit = 20
const EXPORT_STATUS_POLL_INTERVAL = 3000
const EXPORT_STATUS_MAX_ATTEMPTS = 100
const EXPORT_STATUS_DONE = new Set(['SENT', 'FINISHED', 'COMPLETED'])
const EXPORT_STATUS_FAILED = new Set(['ERROR', 'FAILED'])

let timeout = 0

const wait = (milliseconds) => new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds)
})

const normalizeExportStatus = (status) => String(status || '').trim().toUpperCase()

const waitForExportEmailStatus = async ({ enterpriseId, accountId, statusKey }) => {
    for (let attempt = 0; attempt < EXPORT_STATUS_MAX_ATTEMPTS; attempt += 1) {
        const res = await api.get(
            `api/enterprises/${enterpriseId}/scout_accounts/${accountId}/exportmessages_status/${statusKey}/`,
            { timeout: 30000 }
        )
        const status = normalizeExportStatus(res?.data?.status)

        if (EXPORT_STATUS_DONE.has(status)) {
            return res.data
        }

        if (EXPORT_STATUS_FAILED.has(status)) {
            throw new Error(res?.data?.message || 'Não foi possível enviar o e-mail da exportação.')
        }

        if (res.status === 404) {
            throw new Error(res?.data?.message || 'Status da exportação não encontrado.')
        }

        await wait(EXPORT_STATUS_POLL_INTERVAL)
    }

    throw new Error('Não foi possível confirmar o envio do e-mail da exportação.')
}

const loadMore = async (activeChat, messages, setState, windowRef, cursor = 0) => {
    if ([-1, 0].includes(cursor)) return
    const enterprise = getSession().profile.enterprise;
    const params = new URLSearchParams({
        limit: defaultLimit,
        skip: cursor,
        sort: '-messageTimestamp',
        slimResponse: '1',
    });
    const previousScrollHeight = windowRef.current?.scrollHeight || 0
    clearTimeout(timeout)
    setState({ lock: true })
    const res = await api.get(`api/enterprises/${enterprise.id}/scout_accounts/${activeChat.account_id}/messages/${activeChat.id}/?${params.toString()}`);
    if (res.status === 200) {
        setState({ messages: [...res.data.toSorted((x, y) => x.timestamp - y.timestamp), ...messages], cursor: res.data.length > 0 ? defaultLimit + cursor : -1 })

        const nextScrollHeight = windowRef.current?.scrollHeight || 0
        if (previousScrollHeight && nextScrollHeight && windowRef && windowRef.current && cursor !== -1) {
            windowRef.current.scrollBy(0, Math.abs(nextScrollHeight - previousScrollHeight))
        }
    }

    timeout = setTimeout(() => { setState({ lock: false }) }, 1000)
}

const loadChat = async (activeChat, setParent, setState, windowRef, cursor = 0, chatMessageId = null) => {
    if (cursor === -1) return
    const enterprise = getSession().profile.enterprise;
    clearTimeout(timeout)
    setParent({ loading: true, messages: [], lock: true, chatMessageId: null })
    const paramsObj = {
        limit: defaultLimit,
        skip: cursor,
        sort: '-messageTimestamp',
        slimResponse: '1',
    }
    if (chatMessageId) {
        paramsObj.chatMessageId = chatMessageId;
    }
    const params = new URLSearchParams(paramsObj);

    const res = await api.get(`api/enterprises/${enterprise.id}/scout_accounts/${activeChat.account_id}/messages/${activeChat.id}/?${params.toString()}`, { timeout: 50000 });

    setParent({ loading: false })
    if (res.status === 200) {
        setState({ messages: res.data.toSorted((x, y) => x.timestamp - y.timestamp), cursor: res.data.length > 0 ? defaultLimit + cursor : -1 })
        await imagesHaveLoaded()
        dragScrollDown(windowRef)
    }

    if (res.status === 429) {
        useNotification(
            'Limite de solicitações atingido',
            'Você atingiu o limite de solicitações. Por favor, tente novamente mais tarde.',
            'danger'
        );
    } else if (res.status !== 200) {

        useNotification(
            'Não encontrada',
            'Conversa não encontrada.',
            'danger'
        );
    } 
    timeout = setTimeout(() => { setState({ lock: false }) }, 1000)
}

const imagesHaveLoaded = async () => {
    return Array.from(document.querySelectorAll("img")).every(
        (img) => img.complete && img.naturalWidth
    );
}

const submit = async (activeChat, state, handleClose) => {
    const enterprise = getSession().profile.enterprise;

    const chatLabel = activeChat?.name ? `(${activeChat.name})` : ''

    const res = await api.post(`api/enterprises/${enterprise.id}/scout_accounts/${activeChat.account_id}/exportmessages/${activeChat.id}/`, {
        ...state,
        dateini: state.dateini.unix(),
        dateend: state.dateend.unix(),
        numbertext: activeChat.type === 'GROUP' ? `para o grupo ${activeChat?.name || ''}` : `para o número ${activeChat.phone_num} ${chatLabel}`
    })
    if ([200, 202].includes(res.status)) {
        useNotification(
            'Exportação iniciada',
            'A exportação foi iniciada e em breve estará disponível na caixa de e-mail selecionada.',
            'info'
        );

        handleClose()

        const statusKey = res?.data?.key || res?.data?.status_key || res?.data?.statusKey
        if (!statusKey) {
            useNotification(
                'Atenção',
                'Exportação iniciada, mas não foi possível acompanhar o envio do e-mail.',
                'warning'
            );
            return
        }

        try {
            const finalStatus = await waitForExportEmailStatus({
                enterpriseId: enterprise.id,
                accountId: activeChat.account_id,
                statusKey,
            })

            useNotification(
                'Sucesso!',
                finalStatus?.message || 'Conversa exportada e enviada por e-mail com sucesso.',
                'success'
            );
        } catch (error) {
            useNotification(
                'Erro!',
                error.message || 'Não foi possível confirmar o envio do e-mail da exportação.',
                'danger'
            );
        }
    }
    else if (res.status === 409) {
        useNotification(
            'Recurso ocupado',
            'Já existe uma exportação em andamento para sua empresa, aguarde até que ela seja concluída, ou tente novamente em 1 hora.',
            'danger'
        );
    }
    else {
        useNotification(
            'Não encontrada',
            'Conversa não encontrada. Portanto o relatório não pôde ser gerado.',
            'danger'
        );
    }
}

const threshhold = 100

const calcDistBottom = (windowRef) => {
    return (windowRef.current.scrollTop + windowRef.current.offsetHeight + threshhold) >= windowRef.current.scrollHeight
}

const dragScrollDown = (windowRef) => {
    if (windowRef.current)
        windowRef.current.scrollTop = windowRef.current.scrollHeight
}

const getMessageIdentifier = (message) => {
    return message?.id ||
        message?.messageId ||
        message?.chatMessageId ||
        message?.key?.id ||
        [message?.source || message?.key?.remoteJid, message?.timestamp || message?.messageTimestamp, message?.messageType, message?.fromMe].filter(Boolean).join('|')
}

const getMessageTimestamp = (message) => message?.timestamp || message?.messageTimestamp || 0

const mergeMessage = (messages, message) => {
    const identifier = getMessageIdentifier(message)
    const messageIndex = identifier ? messages.findIndex(item => getMessageIdentifier(item) === identifier) : -1
    const nextMessages = [...messages]

    if (messageIndex >= 0) {
        nextMessages[messageIndex] = {...nextMessages[messageIndex], ...message}
        return {messages: nextMessages, inserted: false}
    }

    nextMessages.push(message)
    nextMessages.sort((x, y) => getMessageTimestamp(x) - getMessageTimestamp(y))
    return {messages: nextMessages, inserted: true}
}

let gluedTimeout = 0
let gluedHold = 200

const calcGlued = (glued, setState, windowRef, messages, setParent, parent) => {
    // Get visible messages using DOM and .scout-conversation-box
    let visibleMessages = [];
    if (windowRef.current) {
        const containerRect = windowRef.current.getBoundingClientRect();
        const nodes = windowRef.current.querySelectorAll('.scout-conversation-box');
        nodes.forEach((node, idx) => {
            const rect = node.getBoundingClientRect();
            if (rect.bottom > containerRect.top && rect.top < containerRect.bottom) {
                // Try to get the message index from data-index or fallback to idx
                const msgIdx = node.dataset.index ? parseInt(node.dataset.index) : idx;
                if (messages[msgIdx]) visibleMessages.push(messages[msgIdx]);
            }
        });
    }
    // Calculate date range of visible messages
    if (visibleMessages.length > 0) {
        const timestamps = visibleMessages.map(m => m.timestamp);
        const minTimestamp = Math.min(...timestamps);
        const minDate = dayjs(minTimestamp * 1000).format('YYYY-MM-DD');
        if (parent.analyticsDate !== minDate) {
            setParent({ analyticsDate: minDate });
        }
    }

    clearTimeout(gluedTimeout)
    gluedTimeout = setTimeout(() => {
        const isGlued = windowRef.current && calcDistBottom(windowRef)
        const newState = {}
        if (isGlued !== glued) {
            newState.glued = isGlued
            if (isGlued) newState.messageAmount = 0
        }
        setState(newState)
    }, gluedHold)

}

const ChatWindow = ({ parent, setParent, innerRef }) => {

    const windowRef = useRef()
    const loadedChatRef = useRef(null)
    const scout_ai = getSession().profile.enterprise.platforms.scout_ai;

    const [state, setState] = useReducer(lockedReducer, {
        messages: [],
        confirmEmail: false,
        cursor: 0,
        lock: false,
        messageAmount: 0,
        glued: true,
        editList: null,
        reactionsList: null,
    })

    innerRef.addMessages = useCallback(async (message) => {
        const glued = windowRef.current && calcDistBottom(windowRef)
        const nextState = mergeMessage(state.messages, message)
        setState({ messages: nextState.messages, glued: glued, messageAmount: state.messageAmount + (nextState.inserted ? 1 : 0) })
        if (glued) {
            await imagesHaveLoaded()
            dragScrollDown(windowRef)
        }

    }, [state.messages, state.messageAmount])

    innerRef.updateEventResponses = useCallback((message) => {
        const eventCreationMessageId = message?.eventCreationMessageKey?.id || message?.message?.eventCreationMessageKey?.id || message?.message?.encEventResponseMessage?.eventCreationMessageKey?.id || message?.keyId
        const eventResponses = Array.isArray(message?.eventResponses)
            ? message.eventResponses
            : Array.isArray(message?.pollUpdates)
            ? message.pollUpdates
            : []

        if (!eventCreationMessageId || !eventResponses.length) return

        const mergeEventResponses = (currentResponses = [], incomingResponses = []) => {
            const responseMap = new Map()
            const appendResponses = (responses = []) => {
                if (!Array.isArray(responses)) return

                responses.forEach((response, index) => {
                    const identifier = String(response?.id || response?.jid || response?.responderJid || response?.remoteJid || `response-${index}`).trim()
                    responseMap.set(identifier, { ...(responseMap.get(identifier) || {}), ...response })
                })
            }

            appendResponses(currentResponses)
            appendResponses(incomingResponses)

            return Array.from(responseMap.values())
        }

        const nextMessages = state.messages.map(currentMessage => (
            currentMessage?.id === eventCreationMessageId || currentMessage?.key?.id === eventCreationMessageId
                ? { ...currentMessage, eventResponses: mergeEventResponses(currentMessage?.eventResponses, eventResponses) }
                : currentMessage
        ))
        setState({ messages: nextMessages })
    }, [state.messages])

    useEffect(() => {
        const activeChat = parent.activeChat
        const loadedChat = loadedChatRef.current
        if (activeChat?.id === loadedChat?.id && activeChat?.account_id === loadedChat?.account_id && !parent.chatMessageId) {
            return
        }

        loadedChatRef.current = activeChat
        setState({ cursor: 0, messages: [], lock: true })
        if (activeChat) loadChat(activeChat, setParent, setState, windowRef, 0, parent.chatMessageId)
    }, [parent.activeChat, parent.chatMessageId])

    const chat = parent.activeChat

    return (
      <>
        {state.editList && (
          <EditList
            data={state.editList}
            handleClose={() => setState({ editList: null })}
          />
        )}
        {state.reactionsList && (
          <ReactionsList
            data={state.reactionsList}
            handleClose={() => setState({ reactionsList: null })}
          />
        )}
        {state.confirmEmail && (
          <EmailDialog
            activeChat={parent.activeChat}
            handleClose={() => setState({ confirmEmail: false })}
            submit={submit}
          />
        )}
        {parent.activeChat && !parent.loading ? (
          <Box class="scout-window-main-frame">
            <Box className="scout-window-header">
              <Box
                className="left"
                onClick={() =>
                  setParent({
                    showCRMSidebar: true,
                    showAnalyticsSidebar: false,
                  })
                }
              >
                <span>
                  <Avatar alt={chat.id} src={chat.profile_picture} />
                </span>
                <span className="icon">{groupIcons[chat.type]?.icon}</span>
                <span className="text">{chatName(chat)}</span>
              </Box>
              <Box className="right">
                {scout_ai && (
                  <span>
                    <Tooltip
                      title="Scout AI"
                      placement="top"
                      disableInteractive
                    >
                      <Button
                        className="action-button"
                        onClick={() =>
                          setParent({
                            showAnalyticsSidebar: true,
                            showCRMSidebar: false,
                          })
                        }
                      >
                        <AssessmentIcon />
                      </Button>
                    </Tooltip>
                  </span>
                )}
                <span>
                  <Tooltip
                    title="Exportar histórico de mensagens"
                    placement="top"
                    disableInteractive
                  >
                    <Button
                      className="action-button"
                      onClick={() => setState({ confirmEmail: true })}
                    >
                      <ExportIcon />
                    </Button>
                  </Tooltip>
                </span>
                <span className="scout-align-flex">
                  {chat.phone_num && (
                    <>
                      <span className="icon">
                        <PhoneIphoneIcon />
                      </span>
                      <span className="text">{chat.phone_num}</span>
                    </>
                  )}
                </span>
              </Box>
            </Box>
            <Box className="scout-window-content">
              <List
                onScroll={() =>
                  calcGlued(
                    state.glued,
                    setState,
                    windowRef,
                    state.messages,
                    setParent,
                    parent
                  )
                }
                ref={windowRef}
                className="scout-window-content-list"
                key={`unique-key-by-activechat-${parent.activeChat.id}`}
              >
                {state.cursor !== -1 && !parent.loading && !state.lock && (
                  <ShowComponent
                    key={`show-component-${state.cursor}`}
                    parent={windowRef}
                    loading={state.cursor !== -1}
                    isVisibleHook={(e) => {
                      if (e)
                        loadMore(
                          parent.activeChat,
                          state.messages,
                          setState,
                          windowRef,
                          state.cursor
                        );
                    }}
                  ></ShowComponent>
                )}
                {state.messages
                  .filter(
                    (message) =>
                      !message.type && message.messageType !== "reactionMessage"
                  )
                  .map((message, index, filteredMessages) => (
                    <ScoutMessageComponent
                      message={message}
                      index={index}
                      chatType={chat.type}
                      messages={state.messages}
                      parent={parent}
                      setPrevState={setState}
                      setLoading={(state) => setParent({ loading: state })}
                      previousMessage={
                        index > 0 ? filteredMessages[index - 1] : null
                      }
                    />
                  ))}
              </List>
              {!state.glued && (
                <Box
                  className="scrolldown-box scout"
                  onClick={() => dragScrollDown(windowRef)}
                >
                  <Box sx={{ display: "flex", alignItems: "bottom" }}>
                    <ArrowDownwardIcon />
                  </Box>
                  {state.messageAmount > 0 && (
                    <Box className="messageAmount-badge">
                      {state.messageAmount}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : scout_ai ? (
          parent.activeAccount ? (
            <ScoutAccountDashboard
              dashboardDate={parent.dashboardDate}
              accountId={parent.activeAccount.id}
              setParent={setParent}
            />
          ) : (
            <ScoutGeneralDashboard dashboardDate={parent.dashboardDate} />
          )
        ) : (
          <ChatIntro />
        )}
      </>
    );
}

export default ChatWindow
